import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';

import {toSignal} from '@angular/core/rxjs-interop';

import {OpenGeneratedLessonAction} from '@actions/open-generated-lesson.action';
import {WarnBeforeUnloadDirective} from '@directives/warn-before-unload/warn-before-unload.directive';
import {LessonGenerationStore} from '@stores/lesson-generation.store';
import {isBuilding} from '@utils/lesson-generation-utils';

/**
 * Status of lessons being built from uploaded PDFs, stacked in the lower right.
 * MatSnackBar shows one snackbar at a time, so this renders its own stack.
 */
@Component({
  standalone: true,
  selector: 'lesson-generation-snackbars',
  templateUrl: './lesson-generation-snackbars.component.html',
  styleUrl: './lesson-generation-snackbars.component.scss',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    TranslateModule,
    WarnBeforeUnloadDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonGenerationSnackbarsComponent {
  private readonly _store = inject(LessonGenerationStore);
  private readonly _openLessonAction = inject(OpenGeneratedLessonAction);

  private readonly _jobs = toSignal(this._store.state$, {
    initialValue: this._store.state(),
  });

  protected readonly visibleJobs = computed(() =>
    this._jobs().filter((job) => !job.hidden && job.status !== 'queued'),
  );
  /** Closing the tab would stop a lesson being built */
  protected readonly building = computed(() => isBuilding(this._jobs()));

  protected hide(id: string) {
    this._store.hide(id);
  }

  protected dismiss(id: string) {
    this._store.remove(id);
  }

  protected viewLesson(id: string) {
    this._openLessonAction.execute(id).subscribe();
  }
}
